import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { withSnackbar } from 'notistack';
import { uiState } from 'react-redux-ui-state';
import { Field, reduxForm } from 'redux-form';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@material-ui/core/Icon';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Fab from '@material-ui/core/Fab';
import RotateIcon from '@material-ui/icons/ThreeSixty';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import DownloadIcon from '@material-ui/icons/GetApp';
import PopoutItem from 'components/PopoutItem/PopoutItem';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import BusyButton from 'components/BusyButton/BusyButton';
import ORTypography from 'components/ORTypography/ORTypography';
import { handleClickAway } from 'services/handleClickAway/index';
import { required } from 'services/validationMsgs';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  getInstanceCertificates: PropTypes.func.isRequired,
  getInstanceCertificatesState: PropTypes.func.isRequired,
  createInstanceCertificate: PropTypes.func.isRequired,
  createInstanceClientCertificate: PropTypes.func.isRequired,
  getInstanceCertificateDownload: PropTypes.func.isRequired,
  deleteInstanceCertificate: PropTypes.func.isRequired,
  getPrimaryCertId: PropTypes.func.isRequired,
  instanceId: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  deleteDialogOpen: PropTypes.bool.isRequired,
  setUiState: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  rotateLoading: PropTypes.bool.isRequired,
  downloadLoading: PropTypes.bool.isRequired,
  deleteLoading: PropTypes.bool.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  closeSnackbar: PropTypes.func.isRequired,
  showCADownloadButton: PropTypes.bool,
};

const defaultProps = {
  showCADownloadButton: false,
};

function createCertificateTableCells(certificates, classes) {
  if (!certificates.length) {
    return [];
  }

  const { clientCertificates } = certificates[0];

  if (!clientCertificates.length) {
    return [];
  }

  return clientCertificates.map((clientCertificate) => {
    /*
      When a DB user is deleted, their associated client cert goes into a delete_requested state,
      or 4. As of 2020-02-04, this is the only state where a client cert would still be associated
      with a CA and be invalid.
    */
    const isCertInvalid = clientCertificate.status === 4;
    const certCellText = isCertInvalid ? 'Invalid Certificate' : 'Valid Certificate';
    const certCellIcon = isCertInvalid ? 'verified_user' : 'done';
    const certCellClass = isCertInvalid ? classes.certCellInvalid : classes.certCellValid;
    return (
      <TableRow key={clientCertificate.id} className={classes.tableRow}>
        <TableCell classes={{ root: classes.userCellRoot }} size="small">
          <ORTypography variant="body2">{clientCertificate.username}</ORTypography>
        </TableCell>
        <TableCell className={certCellClass} classes={{ root: classes.certCellRoot }} size="small">
          <Icon>{certCellIcon}</Icon>
          <ORTypography variant="body2">{certCellText}</ORTypography>
        </TableCell>
      </TableRow>
    );
  });
}

export function UnstyledCRCAMgmt({
  getInstanceCertificates,
  getInstanceCertificatesState,
  createInstanceCertificate,
  createInstanceClientCertificate,
  getInstanceCertificateDownload,
  deleteInstanceCertificate,
  getPrimaryCertId,
  instanceId,
  service,
  classes,
  open,
  deleteDialogOpen,
  setUiState,
  handleSubmit,
  rotateLoading,
  downloadLoading,
  deleteLoading,
  enqueueSnackbar,
  closeSnackbar,
  showCADownloadButton,
}) {
  const certificates = getInstanceCertificatesState(instanceId);

  const primaryCA = certificates ? certificates.filter((certificate) => certificate.primary) : [];
  const secondaryCA = certificates
    ? certificates.filter((certificate) => !certificate.primary)
    : [];

  const primaryClientCertCells = createCertificateTableCells(primaryCA, classes);
  const secondaryClientCertCells = createCertificateTableCells(secondaryCA, classes);

  const secondaryHasUsers =
    secondaryCA.length !== 0 ? secondaryCA[0].clientCertificates.length !== 0 : false;

  const userCount = primaryClientCertCells.length;
  const userCountPlural = userCount === 1 ? '' : 's';

  let anchorEl;
  function handleClick() {
    setUiState({
      open: !open,
    });
  }

  const setRef = (node) => {
    anchorEl = node;
  };

  const getAnchorEl = () => anchorEl;

  const options = {
    variant: 'success',
  };

  const action = (key) => (
    <IconButton
      key="close"
      aria-label="Close"
      onClick={() => {
        closeSnackbar(key);
      }}
      color="inherit"
    >
      <CloseIcon />
    </IconButton>
  );

  const submitHandler = (data) => {
    setUiState({
      open: false,
    });
    const formattedCSR = data.csr.replace(/\\n/gm, 'NEWLINE');
    return createInstanceClientCertificate(service, instanceId, formattedCSR, () => {
      enqueueSnackbar('Certificate Created Successfully', { ...options, action });
    }).then(() => {
      getInstanceCertificates(service, instanceId);
    });
  };

  const handleRotateClick = () => {
    setUiState({
      rotateLoading: true,
    });
    createInstanceCertificate(service, instanceId, () => {
      enqueueSnackbar('Certificate Created Successfully', { ...options, action });
    }).then(() => {
      getInstanceCertificates(service, instanceId);
      setUiState({
        rotateLoading: false,
      });
    });
  };

  const handleDownloadClick = () => {
    setUiState({
      downloadLoading: true,
    });
    const CACertId = getPrimaryCertId(instanceId);
    getInstanceCertificateDownload(service.toLowerCase(), instanceId, CACertId)
      .then((data) => {
        if (data.payload.certificate) {
          const element = document.createElement('a');
          element.download = 'primary-cert.crt';
          element.style = 'display: none;';
          const file = new Blob([data.payload.certificate], { type: 'text/plain;charset=utf-8;' }); // eslint-disable-line no-undef
          element.href = window.URL.createObjectURL(file);
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
        }
        setUiState({
          downloadLoading: false,
        });
      })
      .catch((error) => error);
  };

  const handleDeleteClick = () => {
    setUiState({
      deleteDialogOpen: false,
      deleteLoading: true,
    });
    deleteInstanceCertificate(service.toLowerCase(), instanceId, () => {
      enqueueSnackbar('Certificate Scheduled for Deletion', { ...options, action });
    }).then(() => {
      getInstanceCertificates(service, instanceId);
      setUiState({
        deleteLoading: false,
      });
    });
  };

  const handleCancelClick = () => {
    setUiState({
      deleteDialogOpen: false,
    });
  };

  const deleteCertDialog = (
    <Dialog open={deleteDialogOpen}>
      <DialogContent className={classes.dialogContent}>
        There are still users on the Secondary CA. Deleting this will remove their access.
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleCancelClick}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleDeleteClick}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  const createCertForm = (
    <div className={classes.certFormContainer}>
      <ORTypography className={classes.certFormTitle} component="h3" variant="h6">
        Create User Cert by Submitting a CSR
      </ORTypography>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Field
          multiline
          rows={6}
          name="csr"
          type="textarea"
          variant="outlined"
          placeholder="Please enter your Certificate Signing Requests (CSR) to generate a signed cert for the user. This will assign the Primary CA to their cert."
          component={WrappedTextField}
          InputProps={{ classes: { input: classes.input } }}
          props={{ InputLabelProps: { shrink: true } }}
          validate={[required]}
          fullWidth
        />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          className={classes.certFormButton}
        >
          CONFIRM AND GENERATE
        </Button>
      </form>
    </div>
  );

  return (
    <React.Fragment>
      {showCADownloadButton ? (
        <Button
          hover-override="white"
          className={classes.caButton}
          onClick={handleDownloadClick}
          variant="contained"
        >
          DOWNLOAD CA
        </Button>
      ) : (
        <React.Fragment>
          <div className={classes.headerSection}>
            <div className={classes.headerText}>
              <ORTypography className={classes.headerTitle} component="h2" variant="h4">
                Certificate Authority (CA) Management
              </ORTypography>
              <ORTypography variant="body2" className={classes.info}>
                To secure your cluster&rsquo;s node communication you need a Certificate Authority
                (CA) in order to verify identity.
              </ORTypography>
            </div>
            <div className={classes.headerAction}>
              <BusyButton
                loading={rotateLoading}
                className={classes.rotateButton}
                name="submit"
                variant="fab"
                color="secondary"
                size="small"
                tooltip="Rotate CA"
                tooltipPlacement="top-start"
                mini
                onClick={handleRotateClick}
              >
                <RotateIcon />
              </BusyButton>
            </div>
          </div>

          <div className={classes.certificateSection}>
            <ORTypography className={classes.certificateTitle} component="h3" variant="h6">
              Primary Certificate Authority
            </ORTypography>
            <ORTypography variant="body2" className={classes.info}>
              There are {userCount} user
              {userCountPlural} on this Primary CA. Rotating the CA will move the Primary users to
              the Secondary position. It will also create a new Primary CA which will allow access
              for all current users. A user can have access to a Database through either the Primary
              or secondary CA.
            </ORTypography>
            <Table className={classes.certTable}>
              <TableBody>{primaryClientCertCells}</TableBody>
            </Table>
            <div className={classes.primaryCertActions}>
              <BusyButton
                loading={downloadLoading}
                className={classes.downloadButton}
                name="submit"
                variant="fab"
                color="secondary"
                size="small"
                mini
                tooltip="Download"
                tooltipPlacement="left"
                onClick={handleDownloadClick}
              >
                <DownloadIcon />
              </BusyButton>
              <Tooltip title="Create a User Cert by Submitting a CSR.">
                <span>
                  <Fab
                    onClick={handleClick}
                    buttonRef={setRef}
                    size="small"
                    color="secondary"
                    className={classes.createButton}
                  >
                    <div className={classes.iconContainer}>
                      <VerifiedUserIcon />
                      <span className={classes.iconLabel}>What&apos;s this?</span>
                    </div>
                  </Fab>
                </span>
              </Tooltip>
              <PopoutItem
                closeButton={false}
                anchorEl={getAnchorEl}
                handleClick={handleClick}
                handleClickAway={(e) =>
                  handleClickAway(e, anchorEl, () => setUiState({ open: false }))
                }
                placement="bottom"
                open={open}
              >
                {createCertForm}
              </PopoutItem>
            </div>
          </div>

          {secondaryCA.length !== 0 && (
            <div className={classes.certificateSection}>
              <ORTypography className={classes.certificateTitle} component="h3" variant="h6">
                Secondary Certificate Authority
              </ORTypography>
              <Table className={classes.certTable}>
                <TableBody>{secondaryClientCertCells}</TableBody>
              </Table>
              <div className={classes.secondaryCertActions}>
                <div className={classes.deleteButtonWrapper}>
                  <BusyButton
                    loading={deleteLoading}
                    style={{
                      color: 'white',
                      backgroundColor: '#D0021C',
                    }}
                    name="delete"
                    variant="fab"
                    size="small"
                    mini
                    tooltip="Delete Secondary CA"
                    tooltipPlacement="left"
                    onClick={() =>
                      secondaryHasUsers
                        ? setUiState({ deleteDialogOpen: true })
                        : handleDeleteClick()
                    }
                  >
                    <Icon>delete</Icon>
                  </BusyButton>
                </div>
                {deleteCertDialog}
              </div>
            </div>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

UnstyledCRCAMgmt.propTypes = propTypes;
UnstyledCRCAMgmt.defaultProps = defaultProps;

const styles = (theme) => ({
  dialogContent: {
    color: theme.customColors.mediumGrey,
  },
  input: {
    '&::placeholder': {
      fontSize: theme.typography.caption.fontSize,
    },
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: '50%',
    bottom: '0',

    '& :first-child': {
      padding: theme.spacing(1),
      borderRadius: '50%',
      fontSize: '2.75rem',
    },
  },
  iconLabel: {
    paddingTop: '0.25em',
    fontSize: '0.75rem',
    color: 'blue',
    width: '175%',
    textTransform: 'Capitalize',
    fontStyle: 'italic',
  },
  certFormContainer: {
    padding: theme.spacing(1),
  },
  certFormTitle: {
    paddingBottom: theme.spacing(1),
  },
  certFormButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  headerSection: {
    padding: `0 ${theme.spacing(1)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
    borderBottom: `1px solid ${theme.customColors.black}`,
    display: 'block',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  headerText: {
    maxWidth: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '90%',
    },
  },
  headerTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  headerAction: {
    position: 'relative',
  },
  rotateButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      bottom: theme.spacing(1),
      right: theme.spacing(1),
      marginBottom: 'unset',
      marginRight: 'unset',
    },
  },
  createButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  downloadButton: {
    marginRight: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  deleteButtonWrapper: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  header: {
    color: theme.customColors.darkGrey,
  },
  primaryCertActions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  secondaryCertActions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  info: {
    fontStyle: 'italic',
    color: theme.customColors.mediumGrey,
  },
  certificateSection: {
    borderBottom: `1px solid ${theme.customColors.black}`,
    padding: theme.spacing(1),
  },
  certificateTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  certTable: {
    marginTop: theme.spacing(1),
    // we may be able to eliminate or reduce this bottom margin once the action buttons are in place
    marginBottom: theme.spacing(2),
  },
  tableRow: {
    borderBottom: `1px solid ${theme.customColors.lightGrey}`,
    borderTop: `1px solid ${theme.customColors.lightGrey}`,
  },
  userCellRoot: {
    border: 0,
    paddingLeft: 0,
  },
  certCellRoot: {
    display: 'flex',
    alignItems: 'center',
    fontStyle: 'italic',
    justifyContent: 'flex-end',
    border: 0,
  },
  certCellValid: {
    color: theme.customColors.teal,
  },
  certCellInvalid: {
    color: theme.customColors.magenta,
  },
  caButton: {
    color: theme.customColors.black,
    backgroundColor: theme.customColors.white,
    paddingLeft: '2rem',
    paddingRight: '2rem',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
});

export const uiConfig = {
  name: 'createCert',
  state: () => ({
    open: false,
    deleteDialogOpen: false,
    rotateLoading: false,
    downloadLoading: false,
    deleteLoading: false,
  }),
};

export default withSnackbar(
  uiState(uiConfig)(withStyles(styles)(reduxForm({ form: 'createNewCert' })(UnstyledCRCAMgmt))),
);
